import { types, flow, getSnapshot } from "mobx-state-tree"

import * as StringTools from "~/utilities/string"
import * as FileTools from "~/utilities/files"
import * as FilesService from "~/services/files"

import { IMAGE_LOADING_SPINNER } from "~/utilities/constants"

const PersistedImage = types
  .model("PersistedImage", {
    id: types.optional(types.string, StringTools.random),
    base64EncodedImage: types.optional(types.string, ""),
    contentType: types.maybeNull(types.string),
    fileExtension: types.maybeNull(types.string),
    fileSize: types.maybeNull(types.number),
    fileName: types.maybeNull(types.string),
    fileLastModified: types.maybeNull(types.Date),
    persistedPointer: types.maybeNull(types.frozen({})),
    isDirty: types.optional(types.boolean, false)
  })
  .views(self => ({
    get isPersisted() {
      return (
        self.persistedPointer &&
        self.persistedPointer.name &&
        self.persistedPointer.url
      )
    },
    get isImageAttached() {
      return (
        self.isPersisted ||
        (self.base64EncodedImage && self.base64EncodedImage.length)
      )
    },
    get safeFileName() {
      if (!self.fileName) return `${self.id}.${self.fileExtension}`
      return `${StringTools.removeNonAlphaNumeric(self.fileName)}.${
        self.fileExtension
      }`
    }
  }))
  .actions(self => ({
    markDirty() {
      self.isDirty = true
    },
    addAlreadyPersistedImage({ url, name }) {
      self.persistedPointer = { name, url }
    },
    // Fetches encoded image for already persisted images
    fetchAlreadyPersistedImage: flow(function* alreadyUploaded(sessionToken) {
      if (!process.browser) return false
      if (!self.isPersisted) return false
      if (
        self.base64EncodedImage !== IMAGE_LOADING_SPINNER &&
        self.base64EncodedImage !== ""
      ) {
        return false
      }

      self.base64EncodedImage = IMAGE_LOADING_SPINNER
      self.base64EncodedImage = yield FilesService.downloadBase64(
        self.persistedPointer.url,
        sessionToken
      ).catch(err => {
        if (process.env.WINIT_ENV !== "test") {
          console.error(
            "error downloading base64Image from fetchAlreadyPersistedImage: ",
            err
          )
        }
        return ""
      })
      return true
    }),
    /**
     * Takes a web File Api image to store
     */
    attachImageFromFile: flow(function* attachImageGen(file) {
      self.base64EncodedImage = IMAGE_LOADING_SPINNER
      const result = yield FileTools.processFile(file)
      self.base64EncodedImage = result.base64
      self.fileSize = result.size
      self.fileExtension = result.extension
      self.fileName = result.name
      self.fileLastModified = result.lastModified
      self.contentType = result.type // string
      self.isDirty = true
    }),
    /**
     * Uploads image to parse and returns pointer ready to be attached on a collection item
     * @param {string} name The name to attach to a file
     * @returns persisted pointer
     */
    uploadImage: flow(function* uploadImageGen() {
      if (self.isPersisted) {
        const snapshot = getSnapshot(self)
        return snapshot.persistedPointer
      }

      const persistedPointer = yield FilesService.upload(
        self.safeFileName,
        self.contentType,
        self.base64EncodedImage
      )

      self.persistedPointer = persistedPointer

      return persistedPointer
    })
  }))

export default PersistedImage
